import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Loader2, Rocket, Globe, MapPin, Sparkles, CheckCircle, AlertCircle, Eye, TrendingUp, Award, Star, Zap, Target, Users, Brain, Bell } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * GigBoostModal - Premium Boost Modal for Gig Postings
 * 
 * Features:
 * - Card-based layout with premium styling
 * - "What You Get" benefits section
 * - Priority Blast add-on option
 * - AI-powered matching messaging
 */
const GigBoostModal = ({ open, onClose, shiftId, shiftName, onBoostSuccess }) => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedScope, setSelectedScope] = useState('local');
  const [selectedBoost, setSelectedBoost] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [boostStatus, setBoostStatus] = useState(null);
  const [addPriorityBlast, setAddPriorityBlast] = useState(false);
  const [blastPricing, setBlastPricing] = useState(null);

  useEffect(() => {
    if (open && shiftId) {
      fetchPricing();
      fetchBoostStatus();
      fetchBlastPricing();
    }
  }, [open, shiftId]);

  // Auto-select 30 Days (best value) when pricing loads
  useEffect(() => {
    if (pricing) {
      const options = selectedScope === 'local' ? pricing.local.options : pricing.national.options;
      const monthOption = options?.find(o => o.duration === 'month');
      if (monthOption) {
        setSelectedBoost(monthOption);
      }
    }
  }, [pricing, selectedScope]);

  const fetchPricing = async () => {
    setLoading(true);
    try {
      const res = await api.get('/monetization/gig-boost/pricing');
      setPricing(res.data);
    } catch (err) {
      toast.error('Failed to load boost pricing');
    } finally {
      setLoading(false);
    }
  };

  const fetchBoostStatus = async () => {
    try {
      const res = await api.get(`/monetization/gig-boost/status/${shiftId}`);
      setBoostStatus(res.data);
    } catch {}
  };

  const fetchBlastPricing = async () => {
    try {
      const res = await api.get('/monetization/priority-blast/pricing');
      setBlastPricing(res.data);
    } catch {}
  };

  const handleScopeChange = (scope) => {
    setSelectedScope(scope);
    if (pricing) {
      const options = scope === 'local' ? pricing.local.options : pricing.national.options;
      const monthOption = options?.find(o => o.duration === 'month');
      if (monthOption) {
        setSelectedBoost(monthOption);
      }
    }
  };

  const handlePurchase = async () => {
    if (!selectedBoost) {
      toast.error('Please select a boost option');
      return;
    }

    setPurchasing(true);
    try {
      // If Priority Blast is added, we'll handle it separately after boost purchase
      const res = await api.post(`/monetization/gig-boost/purchase/${shiftId}?boost_type=${selectedBoost.id}`);
      
      if (res.data.checkout_url) {
        // Store blast preference for after payment
        if (addPriorityBlast) {
          sessionStorage.setItem('pending_blast_' + shiftId, 'standard');
        }
        window.location.href = res.data.checkout_url;
      } else {
        toast.success('Boost purchased successfully!');
        onBoostSuccess?.();
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to purchase boost');
    } finally {
      setPurchasing(false);
    }
  };

  const getBoostDescription = (duration) => {
    switch (duration) {
      case 'quick':
        return 'Fast visibility for urgent postings';
      case 'week':
        return 'Balanced exposure for standard bookings';
      case 'month':
        return 'Maximum reach and best results';
      default:
        return '';
    }
  };

  const getTotalPrice = () => {
    let total = selectedBoost?.price || 0;
    if (addPriorityBlast && blastPricing?.options?.[0]) {
      total += blastPricing.options[0].price;
    }
    return total;
  };

  const currentOptions = selectedScope === 'local' ? pricing?.local?.options : pricing?.national?.options;

  const renderBoostCard = (option) => {
    const isSelected = selectedBoost?.id === option.id;
    const isBestValue = option.duration === 'month';
    
    return (
      <Card
        key={option.id}
        className={`cursor-pointer transition-all duration-300 relative overflow-visible ${
          isBestValue ? 'scale-[1.02]' : ''
        } ${
          isSelected
            ? isBestValue 
              ? 'ring-2 ring-[#FF10F0] border-[#FF10F0] bg-gradient-to-br from-pink-50 to-cyan-50 shadow-lg shadow-pink-200/50'
              : 'ring-2 ring-[#03DFFC] border-[#03DFFC] bg-cyan-50/50 shadow-lg shadow-cyan-200/50'
            : 'border-gray-200 hover:border-[#03DFFC] hover:shadow-md bg-white'
        }`}
        onClick={() => setSelectedBoost(option)}
        data-testid={`boost-card-${option.id}`}
      >
        {isBestValue && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
            <Badge className="bg-gradient-to-r from-[#FF10F0] to-[#03DFFC] text-white text-xs font-bold px-3 py-1 shadow-lg">
              <Star className="w-3 h-3 mr-1 fill-white" />
              BEST VALUE
            </Badge>
          </div>
        )}
        
        <CardContent className={`p-5 ${isBestValue ? 'pt-6' : ''}`}>
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className={`font-bold ${isBestValue ? 'text-lg text-gray-900' : 'text-base text-gray-800'}`}>
                {option.duration_label}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{option.days} days</p>
            </div>
            <div className="text-right">
              <p className={`font-bold ${
                isBestValue 
                  ? 'text-2xl bg-gradient-to-r from-[#FF10F0] to-[#03DFFC] bg-clip-text text-transparent' 
                  : 'text-xl text-[#03DFFC]'
              }`}>
                ${option.price.toFixed(2)}
              </p>
            </div>
          </div>
          
          <p className="text-sm text-gray-600 mb-3">
            {getBoostDescription(option.duration)}
          </p>
          
          <div className={`flex items-center gap-2 text-xs ${
            isSelected 
              ? isBestValue ? 'text-[#FF10F0]' : 'text-[#03DFFC]'
              : 'text-gray-400'
          }`}>
            {isSelected ? (
              <>
                <CheckCircle className="w-4 h-4" />
                <span className="font-medium">Selected</span>
              </>
            ) : (
              <span>Click to select</span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl bg-white p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="gig-boost-modal">
          <DialogDescription className="sr-only">Dialog actions</DialogDescription>
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[#03DFFC] via-[#FF10F0] to-[#03DFFC] p-6 text-white sticky top-0 z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Rocket className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Boost Your Posting</h2>
              <p className="text-sm text-white/80">Reach the right talent faster — not just more talent</p>
            </div>
          </div>
          
          {shiftName && (
            <p className="text-sm text-white/90 mt-3 bg-white/10 rounded-lg px-3 py-2">
              {shiftName}
            </p>
          )}
        </div>

        <div className="p-6">
          {/* What You Get Section */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#FF10F0]" />
              What You Get
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <div className="w-8 h-8 rounded-full bg-[#03DFFC]/10 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-4 h-4 text-[#03DFFC]" />
                </div>
                <span>Priority placement in talent feed</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <div className="w-8 h-8 rounded-full bg-[#FF10F0]/10 flex items-center justify-center shrink-0">
                  <Award className="w-4 h-4 text-[#FF10F0]" />
                </div>
                <span>Featured / Boosted badge</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                  <Target className="w-4 h-4 text-purple-600" />
                </div>
                <span>Targeted exposure (role + location)</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                  <Zap className="w-4 h-4 text-green-600" />
                </div>
                <span>Faster response from talent</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Brain className="w-4 h-4 text-blue-600" />
                </div>
                <span>AI-powered matching</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-amber-600" />
                </div>
                <span>Increased booking success rate</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3 italic">
              AI-powered matching delivers better applicants, not just more applicants
            </p>
          </div>

          {/* Current Boost Status */}
          {boostStatus?.is_boosted && (
            <div className="bg-gradient-to-r from-[#03DFFC]/10 to-[#FF10F0]/10 border border-[#03DFFC]/30 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 text-gray-800">
                <Sparkles className="w-5 h-5 text-[#FF10F0]" />
                <span className="font-semibold">Currently Boosted</span>
                <Badge className="bg-[#03DFFC] text-white text-xs ml-auto">
                  {boostStatus.scope === 'national' ? 'National' : 'Local'}
                </Badge>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {boostStatus.days_remaining} days remaining • Extends on repurchase
              </p>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-10 h-10 animate-spin text-[#03DFFC]" />
            </div>
          ) : pricing ? (
            <>
              {/* Client Type */}
              <p className="text-xs text-gray-500 mb-4">
                Pricing for: <span className="font-semibold text-gray-700 capitalize">{pricing.client_type.replace('_', ' ')} Client</span>
              </p>

              {/* Scope Tabs */}
              <Tabs value={selectedScope} onValueChange={handleScopeChange} className="mb-6">
                <TabsList className="w-full bg-gray-100 p-1 rounded-xl">
                  <TabsTrigger 
                    value="local" 
                    className="flex-1 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                    data-testid="scope-local"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Local
                  </TabsTrigger>
                  <TabsTrigger 
                    value="national" 
                    className="flex-1 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm"
                    data-testid="scope-national"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    National
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="local" className="mt-4">
                  <p className="text-sm text-gray-600 mb-4">{pricing.local.description}</p>
                </TabsContent>
                <TabsContent value="national" className="mt-4">
                  <p className="text-sm text-gray-600 mb-4">{pricing.national.description}</p>
                </TabsContent>
              </Tabs>

              {/* Boost Cards */}
              <div className="space-y-4 mb-6">
                {currentOptions?.sort((a, b) => a.days - b.days).map(renderBoostCard)}
              </div>

              {/* Priority Blast Add-on */}
              {blastPricing && (
                <div className={`border-2 rounded-xl p-4 mb-6 transition-all ${
                  addPriorityBlast 
                    ? 'border-[#39FF14] bg-[#39FF14]/5 shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        addPriorityBlast ? 'bg-[#39FF14]/20' : 'bg-gray-100'
                      }`}>
                        <Bell className={`w-5 h-5 ${addPriorityBlast ? 'text-green-600' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                          Priority Blast
                          <Badge className="bg-[#39FF14] text-gray-900 text-[10px]">ADD-ON</Badge>
                        </h4>
                        <p className="text-xs text-gray-500">Instantly notify favorites & matched talent</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-[#03DFFC]">+${blastPricing.options[0].price.toFixed(2)}</span>
                      <Switch 
                        checked={addPriorityBlast} 
                        onCheckedChange={setAddPriorityBlast}
                        data-testid="priority-blast-toggle"
                      />
                    </div>
                  </div>
                  {addPriorityBlast && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <ul className="text-xs text-gray-600 space-y-1">
                        {blastPricing.options[0].features.map((f, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* CTA Button */}
              <div className="space-y-3">
                <Button
                  onClick={handlePurchase}
                  disabled={!selectedBoost || purchasing}
                  className="w-full h-14 text-lg font-bold bg-gradient-to-r from-[#03DFFC] to-[#FF10F0] hover:from-[#02C5E0] hover:to-[#E00ED8] text-white shadow-lg shadow-pink-300/30 transition-all duration-300 hover:shadow-xl hover:shadow-pink-300/40"
                  data-testid="purchase-boost-btn"
                >
                  {purchasing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : selectedBoost ? (
                    <>
                      <Rocket className="w-5 h-5 mr-2" />
                      Get Boosted — ${getTotalPrice().toFixed(2)}
                    </>
                  ) : (
                    'Select a Boost Option'
                  )}
                </Button>
                
                <Button 
                  variant="ghost" 
                  onClick={onClose} 
                  className="w-full text-gray-500 hover:text-gray-700"
                >
                  Maybe Later
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-gray-400" />
              <p>Unable to load pricing</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GigBoostModal;
